import type {EndingBody} from '../../../data/api/StudioApi';
import type {EndingType, Question, Reel} from '../types';
import {endingEditorText} from './endingEditorText';

/** An unsaved knowledge check or attestation, as edited on the page. */
export type EndingDraft = {question: Question} | {statement: string};

/** Every knowledge check offers four answers. */
export const OPTION_COUNT = 4;

export const EMPTY_QUESTION: Question = {
  stem: '',
  options: ['', '', '', ''],
  correctIndex: 0,
  feedback: '',
  cites: [],
};

/** Older questions had three answers: show empty slots up to four. */
export function withAllOptions(question: Question): Question {
  const options = question.options.slice(0, OPTION_COUNT);
  while (options.length < OPTION_COUNT) options.push('');
  return {...question, options};
}

export function normalizeQuestion<
  T extends Omit<Question, 'feedback'> & {feedback?: string},
>(question: T): T {
  const retained = question.options
    .map((option: string, index: number) => ({value: option.trim(), index}))
    .filter(option => option.value);
  const correctIndex = retained.findIndex(
    option => option.index === question.correctIndex,
  );
  if (correctIndex < 0) throw new Error(endingEditorText.correctAnswerBlank);
  if (retained.length < OPTION_COUNT)
    throw new Error(endingEditorText.fillAllOptions);
  if (!question.stem.trim()) throw new Error(endingEditorText.enterQuestion);
  return {
    ...question,
    stem: question.stem.trim(),
    options: retained.map(option => option.value),
    correctIndex,
  };
}

/** The PUT body for an ending draft; throws when the draft is incomplete. */
export function endingRequestBody(
  type: EndingType | undefined,
  draft: EndingDraft,
  reel: Reel,
): EndingBody {
  if (type === 'knowledge_check' && 'question' in draft)
    return {question: normalizeQuestion(draft.question)};
  const statement = 'statement' in draft ? draft.statement.trim() : '';
  if (!statement) throw new Error(endingEditorText.enterStatement);
  return {attestation: {statement, cites: reel.attestation?.cites || []}};
}
